import { useCallback, useEffect, useRef, useState } from "react";
import { coverStyle } from "@/lib/cover";
import { useParams } from "react-router-dom";
import api, { photoFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useInfiniteWindow } from "@/lib/useInfiniteWindow";
import { toast } from "sonner";
import { Heart, Calendar, MapPin, Upload, X, ExternalLink, Lock, LogIn } from "lucide-react";
import { to12h } from "@/lib/time";

export default function PublicWedding() {
  const { slug } = useParams();
  const { user, login, register } = useAuth();
  const [wedding, setWedding] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | auth | forbidden | unpublished
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [upFirst, setUpFirst] = useState("");
  const [upLast, setUpLast] = useState("");
  const [upEmail, setUpEmail] = useState("");
  const [albumFilter, setAlbumFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [thanks, setThanks] = useState(false);
  const fileRef = useRef();

  const albumList = [...new Set(photos.map((p) => p.album).filter(Boolean))].sort();
  const ordered = [...albumList.flatMap((a) => photos.filter((p) => p.album === a)), ...photos.filter((p) => !p.album)];
  const shown = albumFilter === "all" ? ordered : photos.filter((p) => (p.album || "") === albumFilter);
  const { count, sentinelRef, hasMore } = useInfiniteWindow(shown.length, 48);
  const visible = shown.slice(0, count);

  const loadPhotos = useCallback(() => api.get(`/public/photos/${slug}`).then(({ data }) => setPhotos(data)).catch(() => setPhotos([])), [slug]);

  const load = useCallback(() => {
    setStatus("loading");
    api.get(`/public/event/${slug}`)
      .then(({ data }) => { setWedding(data); setStatus("ok"); loadPhotos(); })
      .catch((e) => {
        const s = e.response?.status;
        const code = e.response?.data?.detail;
        if (s === 401) setStatus("auth");
        else if (s === 403) setStatus("forbidden");
        else if (s === 404 && code === "NOT_PUBLISHED") setStatus("unpublished");
        else setStatus("notfound");
      });
  }, [slug, loadPhotos]);

  useEffect(() => { load(); }, [load, user]);

  const isPrivate = !!wedding?.private_page;

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!isPrivate && (!upFirst.trim() || !upLast.trim() || !upEmail.trim())) {
      toast.error("Please enter your first name, last name and email before adding photos.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("uploader_first", upFirst.trim());
        fd.append("uploader_last", upLast.trim());
        fd.append("uploader_email", upEmail.trim());
        await api.post(`/public/photos/${slug}/upload`, fd);
      }
      setThanks(true); setTimeout(() => setThanks(false), 6000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (status === "loading") return <Centered><p className="font-serif text-3xl text-stone-300">Loading…</p></Centered>;
  if (status === "notfound") return <Centered><p className="font-serif text-3xl text-stone-500">Event not found</p></Centered>;
  if (status === "unpublished") return (
    <Centered>
      <div className="text-center max-w-sm px-6" data-testid="event-unpublished">
        <div className="w-14 h-14 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86] mx-auto mb-4"><Calendar size={22} /></div>
        <h2 className="font-serif text-3xl">This event page isn't published yet</h2>
        <p className="text-sm text-stone-500 mt-3">Check back soon — the host is still putting the finishing touches on it.</p>
      </div>
    </Centered>
  );
  if (status === "auth") return <PrivateGate onLogin={login} onRegister={register} />;
  if (status === "forbidden") return (
    <Centered>
      <div className="text-center max-w-sm px-6" data-testid="private-not-invited">
        <div className="w-14 h-14 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86] mx-auto mb-4"><Lock size={22} /></div>
        <h2 className="font-serif text-3xl">This event is private</h2>
        <p className="text-sm text-stone-500 mt-3">Only invited guests can view this page. If you were invited, sign in with the email address your host used to invite you.</p>
        <a href="/login" className="inline-block mt-6 text-sm text-[#8C9A86] hover:underline" data-testid="private-signin-link">Sign in with a different account →</a>
      </div>
    </Centered>
  );

  const accent = wedding.theme_color || "#8C9A86";

  return (
    <div className="min-h-screen" style={{ background: "#FDFBF7" }}>
      {/* Hero */}
      <div className="relative h-[70vh] min-h-[420px] overflow-hidden">
        <img src={wedding.cover_image_url} alt="venue" className="absolute inset-0 w-full h-full object-cover" style={coverStyle(wedding)} />
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative h-full flex flex-col items-center justify-center text-center text-white px-6">
          <Heart size={24} fill="white" className="mb-4" />
          <p className="overline text-white/70">{wedding.type === "wedding" ? "The Wedding of" : "You're invited to celebrate"}</p>
          <h1 className="font-serif text-6xl sm:text-7xl tracking-tight mt-3" style={{ fontFamily: wedding.theme_font || undefined }}>{wedding.title}</h1>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 mt-6 text-sm text-white/85">
            {wedding.date && <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDate(wedding.date)}</span>}
            {wedding.venue_name && <span className="flex items-center gap-1.5"><MapPin size={14} /> {wedding.venue_name}</span>}
          </div>
        </div>
      </div>

      {/* Story */}
      {wedding.story && (
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <p className="overline mb-4">Our story</p>
          <p className="font-serif text-2xl sm:text-3xl leading-relaxed text-stone-700">{wedding.story}</p>
        </div>
      )}

      {/* Schedule */}
      {wedding.sub_events?.length > 0 && (
        <div className="max-w-3xl mx-auto px-6 pb-16">
          <p className="overline text-center mb-8">Schedule</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {wedding.sub_events.map((s) => (
              <div key={s.id} className="bg-white border border-[#E8E6E1] rounded-lg p-6 text-center">
                <p className="font-serif text-2xl text-stone-800">{s.name}</p>
                {s.time && <p className="text-sm mt-2" style={{ color: accent }}>{to12h(s.time)}</p>}
                {(s.venue_name || s.location) && <p className="text-xs text-stone-500 mt-1">{s.venue_name || s.location}</p>}
                {s.address && <p className="text-xs text-stone-400 mt-0.5">{s.address}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Registry */}
      {wedding.registry?.length > 0 && (
        <div className="max-w-3xl mx-auto px-6 pb-16">
          <p className="overline text-center mb-6">Gift registry</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {wedding.registry.map((r, i) => (
              <a key={i} href={r.url} target="_blank" rel="noreferrer" data-testid={`registry-link-${i}`}
                className="group flex items-center justify-between bg-white border border-[#E8E6E1] rounded-lg px-5 py-4 hover:border-[#8C9A86] transition-colors">
                <span className="text-sm font-medium text-stone-700">{r.label}</span>
                <ExternalLink size={15} className="text-stone-400 group-hover:text-[#8C9A86] transition-colors" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Gallery + upload */}
      <div className="max-w-5xl mx-auto px-6 pb-24">
        <div className="text-center mb-8">
          <p className="overline mb-2">Shared memories</p>
          <h2 className="font-serif text-4xl tracking-tight">Photo gallery</h2>
          <p className="text-sm text-stone-500 mt-3 max-w-md mx-auto">Share your favourite moments. Photos appear here once the host approves them.</p>

          <div className="mt-6 max-w-xl mx-auto">
            {!isPrivate && (
              <>
                <p className="text-xs text-stone-500 mb-3">Please enter your name and email so hosts can see who sent each photo. Your name and email are only visible to hosts, not other guests.</p>
                <div className="grid sm:grid-cols-3 gap-2 mb-3">
                  <input value={upFirst} onChange={(e) => setUpFirst(e.target.value)} placeholder="First name" data-testid="uploader-first"
                    className="rounded-full border border-[#E8E6E1] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
                  <input value={upLast} onChange={(e) => setUpLast(e.target.value)} placeholder="Last name" data-testid="uploader-last"
                    className="rounded-full border border-[#E8E6E1] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
                  <input value={upEmail} onChange={(e) => setUpEmail(e.target.value)} type="email" placeholder="Email" data-testid="uploader-email"
                    className="rounded-full border border-[#E8E6E1] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
                </div>
              </>
            )}
            <div className="flex items-center justify-center">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="guest-upload-btn" style={{ backgroundColor: accent }}
                className="flex items-center gap-2 rounded-full text-white px-5 py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                <Upload size={16} /> {uploading ? "Uploading…" : "Add your photos"}
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onUpload} data-testid="guest-file-input" />
            </div>
          </div>
          {thanks && <p className="text-sm text-[#8C9A86] mt-3 fade-up" data-testid="upload-thanks">Thank you! Your photos are awaiting the host's approval.</p>}
        </div>

        {albumList.length > 0 && (
          <div className="flex justify-center mb-6">
            <select value={albumFilter} onChange={(e) => setAlbumFilter(e.target.value)} data-testid="public-album-filter"
              className="rounded-full border border-[#E8E6E1] bg-white px-4 py-2 text-sm outline-none focus:border-[#8C9A86]">
              <option value="all">All photos</option>
              {albumList.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        )}

        {photos.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E8E6E1] rounded-lg p-16 text-center text-sm text-stone-400">
            No photos yet. Be the first to share a memory.
          </div>
        ) : albumFilter === "all" ? (
          <>
            {albumList.map((a) => {
              const ph = visible.filter((p) => p.album === a);
              if (!ph.length) return null;
              return (
                <div key={a} className="mb-10" data-testid={`public-album-section-${a}`}>
                  <p className="overline mb-3">{a}</p>
                  <PhotoGrid photos={ph} onOpen={setLightbox} />
                </div>
              );
            })}
            {visible.some((p) => !p.album) && <PhotoGrid photos={visible.filter((p) => !p.album)} onOpen={setLightbox} />}
            {hasMore && <div ref={sentinelRef} data-testid="gallery-sentinel" className="h-10" />}
          </>
        ) : (
          <>
            <PhotoGrid photos={visible} onOpen={setLightbox} />
            {hasMore && <div ref={sentinelRef} data-testid="gallery-sentinel" className="h-10" />}
          </>
        )}
      </div>

      <footer className="text-center py-10 border-t border-[#E8E6E1]">
        <div className="flex items-center justify-center gap-2 text-stone-400">
          <Heart size={14} fill="#8C9A86" className="text-[#8C9A86]" />
          <span className="font-serif text-lg">Evermore</span>
        </div>
      </footer>

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" onClick={() => setLightbox(null)}>
          <button className="absolute top-6 right-6 text-white/80 hover:text-white"><X size={28} /></button>
          <img src={photoFileUrl(lightbox.id)} alt="" className="max-h-[85vh] max-w-full rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}

function PhotoGrid({ photos, onOpen }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {photos.map((p, i) => (
        <div key={p.id} data-testid={`public-photo-${p.id}`}
          className="aspect-square rounded-lg overflow-hidden bg-stone-100 fade-up cursor-pointer group"
          style={{ animationDelay: `${(i % 48) * 30}ms` }} onClick={() => onOpen(p)}>
          <img src={photoFileUrl(p.id, { thumb: true })} alt="" loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
      ))}
    </div>
  );
}

function PrivateGate({ onLogin, onRegister }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!email || !password || (mode === "register" && !name)) { toast.error("Please fill in all fields."); return; }
    setBusy(true);
    try {
      if (mode === "register") await onRegister(name, email, password);
      else await onLogin(email, password);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Centered>
      <div className="bg-white border border-[#E8E6E1] rounded-xl p-6 sm:p-8 w-full max-w-md fade-up" data-testid="private-auth-gate">
        <div className="w-12 h-12 rounded-full bg-[#8C9A86]/12 grid place-items-center text-[#8C9A86] mx-auto mb-4"><LogIn size={20} /></div>
        <h2 className="font-serif text-2xl text-center">This is a private event</h2>
        <p className="text-sm text-stone-500 text-center mt-2">Please sign in with the email your host invited you with to view this page.</p>
        <div className="space-y-3 mt-6">
          {mode === "register" && <GateField label="Your name" value={name} onChange={setName} testid="private-name" />}
          <GateField label="Email" type="email" value={email} onChange={setEmail} testid="private-email" />
          <GateField label="Password" type="password" value={password} onChange={setPassword} testid="private-password" />
        </div>
        <button onClick={go} disabled={busy} data-testid="private-submit"
          className="mt-6 w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40">
          {busy ? "Please wait…" : (mode === "register" ? "Create account & continue" : "Sign in & continue")}
        </button>
        <p className="text-center text-sm text-stone-500 mt-4">
          {mode === "register" ? "Already have an account?" : "New to Evermore?"}{" "}
          <button onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="private-toggle" className="text-[#8C9A86] hover:underline">
            {mode === "register" ? "Sign in" : "Create one"}
          </button>
        </p>
      </div>
    </Centered>
  );
}

function GateField({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen grid place-items-center px-4" style={{ background: "#FDFBF7" }}>{children}</div>;
}

function formatDate(d) {
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return d; }
}
